// Selector de estilo de subtítulos para el modo "Video con influencer" (V2).
//
// Mismo chrome que `VoicePickerV2`/`GanchoPicker` (portal: bottom sheet en
// mobile, panel lateral derecho en desktop) con el contenido del menú de
// captions del flujo V1 (`ConfigurarVideoInfluencerVideo`): chips de
// categoría + grilla de previews GIF (12:5). Elegir un estilo lo marca; el
// panel se cierra con "Listo" o el overlay — así se pueden comparar varios
// GIFs sin reabrir (mismo comportamiento que V1). Ver ADR-537.

import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, X } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';
import {
  CAPTION_CATEGORIES,
  CAPTION_STYLES,
} from '../../../constants/captionStyles';

export interface CaptionStylePickerV2Props {
  open: boolean;
  onClose: () => void;
  selectedCaptionId: string;
  onSelect: (id: string) => void;
}

export const CaptionStylePickerV2: React.FC<CaptionStylePickerV2Props> = ({
  open,
  onClose,
  selectedCaptionId,
  onSelect,
}) => {
  const { mounted, animated } = useExitTransition(open, 300);
  // Arranca en la categoría del estilo ya seleccionado.
  const [category, setCategory] = useState<string>(
    () =>
      CAPTION_STYLES.find((c) => c.id === selectedCaptionId)?.category ??
      'Simple',
  );

  const items = useMemo(
    () => CAPTION_STYLES.filter((c) => c.category === category),
    [category],
  );

  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
          <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black md:flex-1">
            Estilo de subtítulos
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-[#3C3C43]/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />

        {/* Chips de categoría */}
        <div className="px-5 pt-4 pb-1 flex flex-wrap gap-1.5 shrink-0">
          {CAPTION_CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition-colors ${
                category === cat
                  ? 'bg-brand-primary text-white'
                  : 'bg-black/[0.05] text-[#3C3C43]/60 hover:bg-black/[0.08]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grilla de previews — `min-h-0` habilita el scroll interno (sin
            eso el flex item no se achica y la lista no scrollea). Altura
            uniforme vía `aspect-ratio` INLINE: la clase `aspect-[12/5]` no
            compila en este setup de Tailwind (verificado contra el CSS del
            build), cada GIF quedaba con su alto nativo. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {items.map((cap) => {
            const isSelected = selectedCaptionId === cap.id;
            return (
              <button
                key={cap.id}
                type="button"
                onClick={() => onSelect(cap.id)}
                className={`relative shrink-0 rounded-2xl border-2 overflow-hidden transition-colors ${
                  isSelected
                    ? 'border-brand-primary'
                    : 'border-transparent ring-1 ring-black/[0.08] hover:border-black/[0.15]'
                }`}
              >
                <img
                  src={cap.gifPath}
                  alt={`Estilo ${cap.category}`}
                  className="w-full object-cover"
                  style={{ aspectRatio: '12 / 5' }}
                  loading="lazy"
                />

                {isSelected && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                    <Check className="w-3 h-3" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 pt-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center py-3 px-5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-semibold shadow-sm transition-colors"
          >
            Listo
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};
